import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

interface LanguageOption {
  country: string;
  language: string;
  code: string;
}

const LanguageSelector: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const languageOptions: LanguageOption[] = [
    { country: 'USA', language: 'English', code: 'en-us' },
    { country: 'USA', language: 'Spanish', code: 'es-us' },
    { country: 'India', language: 'English', code: 'en-in' },
    { country: 'India', language: 'Hindi', code: 'hi-in' },
    { country: 'France', language: 'French', code: 'fr-fr' },
    { country: 'India', language: 'Marathi', code: 'mr-in' },
  ];

  const handleLanguageSelect = (code: string) => {
    setIsOpen(false);
    i18n.changeLanguage(code);
    localStorage.setItem('locale-streakspark', code);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 bg-white/60 dark:bg-neutral-800/60 backdrop-blur-sm rounded-full shadow-soft hover:shadow-medium transition-all duration-300"
      >
        <svg className="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.22 18.22 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
        </svg>
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {t('languageSelector.selectLanguage')}
        </span>
      </button>

      {isOpen && (
        <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 w-64 bg-white dark:bg-neutral-800 rounded-2xl shadow-large border border-neutral-200 dark:border-neutral-700 z-50">
          <div className="p-4">
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
              {t('languageSelector.chooseLanguage')}
            </h3>
            <div className="space-y-2">
              {languageOptions.map((option) => (
                <button
                  key={`${option.country}-${option.language}`}
                  onClick={() => handleLanguageSelect(option.code)}
                  className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                      <span className="text-xs font-bold text-white">
                        {option.country === 'USA' ? '🇺🇸' : option.country === 'France' ? '🇫🇷' : '🇮🇳'}
                      </span>
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                        {option.country} - {option.language}
                      </div>
                    </div>
                  </div>
                  {i18n.language === option.code && (
                    <svg className="w-4 h-4 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default LanguageSelector;
